import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle, Building, Edit, MessageCircle, Plus, Printer, Target, Trash2, Users, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import SortableEntryList from './SortableEntryList';
import { Checkbox } from './ui/checkbox';
import { API_BASE_URL } from '../config';
import EntryImageCard from './EntryImageCard';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';
import OrganizationsInlineManager from './OrganizationsInlineManager';

const API = `${API_BASE_URL}/api`;

const LiaisonOfficerModule = ({ theme, exerciseId, exerciseName }) => {
  const [liaisonData, setLiaisonData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [activeCategory, setActiveCategory] = useState('incident-information');

  // Helper function to format timestamp in 24-hour format (e.g., "2024-10-27 23:15")
  const format24HourTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Form state
  const [formData, setFormData] = useState({
    category: 'incident-information',
    title: '',
    description: '',
    priority: 'medium',
    status: 'active',
    timestamp: '',
    location: '',
    contact_info: '',
    contact_name: '',
    contact_phone: '',
    notes: '',
    date_from: '',
    date_to: '',
    image: ''
  });

  const [errors, setErrors] = useState({});

  // Categories and their fields
  const categories = {
    'incident-information': {
      label: 'Incident Information',
      icon: AlertCircle,
      fields: [
        { key: 'incident_briefing', label: 'Incident Briefing from IC/UC', type: 'textarea', required: true, placeholder: 'information from the Incident Commander or Unified Command on the current situation(s) involving the incident' },
        { key: 'incident_action_plan', label: 'Incident Action Plan (IAP)', type: 'textarea', required: true, placeholder: 'information regarding organization and assignments, communications plans, and safety and medical plans, etc' },
        { key: 'incident_objectives', label: 'Incident Objectives', type: 'textarea', required: true, placeholder: 'information regarding the Incident Commanders current objectives' }
      ]
    },
    'situational-awareness': {
      label: 'Situational Awareness',
      icon: Target,
      fields: [
        { key: 'situational_awareness_reports', label: 'Situation Reports & Status Updates', type: 'textarea', required: true, placeholder: 'list of any situational reports or changes in the status of the incident' },
        { key: 'current_potential_issues', label: 'Current/Potential Interorganizational Issues', type: 'textarea', required: true, placeholder: 'list of current / potential / conflicting issues concerning the organization' },
        { key: 'updates_from_other_liaisons', label: 'Updates from Other Liaison Officers', type: 'textarea', required: false, placeholder: 'collection of information from other Liaison Officers assigned to the Incident' }
      ]
    },
    'liaison-office-management': {
      label: 'Liaison Office Management',
      icon: Building,
      fields: [
        { key: 'liaison_office_status', label: 'Liaison Office Status', type: 'textarea', required: true, placeholder: 'This can refer to several things: a government-sponsored service, the legal status of a specific type of international office, or an office within a larger organization.' },
        { key: 'liaison_staff_resources', label: 'Liaison Staff & Resources', type: 'textarea', required: true, placeholder: 'resources refer to the personnel, equipment, supplies, and facilities used to manage an incident' },
        { key: 'equipment_supplies', label: 'Equipment & Supplies', type: 'textarea', required: false, placeholder: 'equipment and supplies needed to function in the capacity of Liaison Officer and staff' },
        { key: 'organizational_structure', label: 'Organizational Structure', type: 'textarea', required: false, placeholder: 'the organization of command structure' }
      ]
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-500' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
    { value: 'high', label: 'High', color: 'text-orange-500' },
    { value: 'critical', label: 'Critical', color: 'text-red-500' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'text-green-500' },
    { value: 'pending', label: 'Pending', color: 'text-yellow-500' },
    { value: 'completed', label: 'Completed', color: 'text-blue-500' },
    { value: 'archived', label: 'Archived', color: 'text-gray-500' }
  ];

  // Load liaison data on component mount
  useEffect(() => {
    loadLiaisonData();
    loadExerciseOrganizations();
  }, [exerciseId]);

  const [exerciseOrganizations, setExerciseOrganizations] = useState([]);

  const loadExerciseOrganizations = async () => {
    if (!exerciseId) return;
    try {
      const response = await axios.get(`${API}/exercise-builder/${exerciseId}`);
      const orgs = Array.isArray(response.data?.organizations) ? response.data.organizations : [];
      setExerciseOrganizations(orgs);
    } catch (error) {
      console.error('Error loading exercise organizations:', error);
      setExerciseOrganizations([]);
    }
  };

  const loadLiaisonData = async () => {
    if (!exerciseId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/liaison-officer?exercise_id=${exerciseId}`);
      setLiaisonData(response.data);
    } catch (error) {
      console.error('Error loading liaison officer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: activeCategory,
      title: '',
      description: '',
      priority: 'medium',
      status: 'active',
      timestamp: new Date().toISOString().slice(0, 16),
      location: '',
      contact_info: '',
      contact_name: '',
      contact_phone: '',
      notes: '',
      date_from: '',
      date_to: '',
      image: ''
    });
    setErrors({});
    setEditingEntry(null);
  };

  const validateForm = () => {
    const newErrors = {};
    const categoryFields = categories[formData.category]?.fields || [];

    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.description) newErrors.description = 'Description is required';

    // Validate category-specific required fields
    categoryFields.forEach(field => {
      if (field.required && !formData[field.key]) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const entryData = {
        ...formData,
        exercise_id: exerciseId,
        timestamp: formData.timestamp || new Date().toISOString()
      };

      if (editingEntry) {
        await axios.put(`${API}/liaison-officer/${editingEntry.id}`, entryData);
      } else {
        await axios.post(`${API}/liaison-officer`, entryData);
      }

      resetForm();
      setShowForm(false);
      loadLiaisonData();
    } catch (error) {
      console.error('Error saving liaison officer entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setFormData({
      category: entry.category || 'incident-information',
      title: entry.title || '',
      description: entry.description || '',
      priority: entry.priority || 'medium',
      status: entry.status || 'active',
      timestamp: entry.timestamp ? entry.timestamp.slice(0, 16) : '',
      location: entry.location || '',
      contact_info: entry.contact_info || '',
      contact_name: entry.contact_name || '',
      contact_phone: entry.contact_phone || '',
      notes: entry.notes || '',
      date_from: entry.date_from || '',
      date_to: entry.date_to || '',
      image: entry.image || '',
      ...Object.fromEntries(
        Object.keys(categories).flatMap(cat => 
          categories[cat].fields.map(field => [field.key, entry[field.key] || ''])
        )
      )
    });
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      setLoading(true);
      const token = secureStorage.getToken();
      await axios.delete(`${API}/liaison-officer/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      await loadLiaisonData();
      alert('Entry deleted successfully');
    } catch (error) {
      console.error('Error deleting liaison officer entry:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to delete this entry. Only Agency Admins and Super Admins can delete entries.');
      } else if (error.response?.status === 404) {
        alert('Entry not found. It may have already been deleted.');
      } else {
        alert('Failed to delete entry. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (entry = null) => {
    const printContent = entry ? 
      generateSingleEntryPrint(entry) : 
      generateMultipleEntriesPrint();
    
    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, `
      <html>
        <head>
          <title>Liaison Officer Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .entry { margin-bottom: 30px; border: 1px solid #ddd; padding: 15px; page-break-inside: avoid; }
            .field { margin-bottom: 8px; }
            .label { font-weight: bold; display: inline-block; width: 150px; }
            .category { background: #f5f5f5; padding: 10px; margin-bottom: 15px; border-left: 4px solid #ef4444; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
            @media print {
              .entry { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>Powered by EXRSIM - Emergency Response Simulator</p>
          </div>
        </body>
      </html>
    `);
    printWindow.print();
  };

  const generateSingleEntryPrint = (entry) => {
    return `
      <div class="header">
        <h1>Liaison Officer Report</h1>
        <h3>Exercise: ${exerciseName || exerciseId}</h3>
      </div>
      ${generateEntryContent(entry)}
    `;
  };

  const generateEntryContent = (entry) => {
    const category = categories[entry.category];
    return `
      <div class="entry">
        <div class="category">
          <h3>${category?.label || entry.category}</h3>
        </div>
        <div class="field"><span class="label">Title:</span> ${entry.title || 'N/A'}</div>
        <div class="field"><span class="label">Description:</span> ${entry.description || 'N/A'}</div>
        <div class="field"><span class="label">Priority:</span> ${entry.priority || 'N/A'}</div>
        <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
        <div class="field"><span class="label">Operational Period From:</span> ${entry.date_from ? entry.date_from.replace("T", " ").slice(0, 16) : "N/A"}</div>
        <div class="field"><span class="label">Operational Period To:</span> ${entry.date_to ? entry.date_to.replace("T", " ").slice(0, 16) : "N/A"}</div>
        <div class="field"><span class="label">Location:</span> ${entry.location || 'N/A'}</div>
        <div class="field"><span class="label">Contact:</span> ${[entry.contact_name || entry.contact_info, entry.contact_phone].filter(Boolean).join(" - ") || "N/A"}</div>
        <div class="field"><span class="label">Notes:</span> ${entry.notes || 'None'}</div>
        ${category?.fields.map(field => 
          `<div class="field"><span class="label">${field.label}:</span> ${entry[field.key] || 'N/A'}</div>`
        ).join('') || ''}
      </div>
    `;
  };

  const generateOrganizationsPrint = () => {
    const orgs = exerciseOrganizations || [];
    if (orgs.length === 0) return '';
    const esc = (s) => String(s == null ? '' : s);
    return `
      <div class="entry">
        <div class="category" style="border-left-color:#3b82f6;">
          <h3>Organization Coordination (${orgs.length})</h3>
        </div>
        ${orgs.map((org) => {
          const persons = Array.isArray(org.persons) ? org.persons : [];
          return `
            <div style="margin: 12px 0; padding: 10px; border: 1px solid #e5e7eb; border-radius: 6px; page-break-inside: avoid;">
              <div style="display:flex; align-items:flex-start; gap:12px;">
                ${org.organization_image ? `<img src="${esc(org.organization_image)}" alt="Logo" style="width:56px;height:56px;object-fit:cover;border-radius:4px;border:1px solid #ddd;"/>` : ''}
                <div style="flex:1;">
                  <div style="font-weight:bold; font-size:15px;">${esc(org.name) || 'Unnamed Organization'}</div>
                  ${org.description ? `<div style="color:#4b5563; font-size:13px; margin-top:4px;">${esc(org.description)}</div>` : ''}
                  <div style="color:#6b7280; font-size:12px; margin-top:4px;">${persons.length} ${persons.length === 1 ? 'person' : 'persons'}</div>
                </div>
              </div>
              ${persons.length > 0 ? `
                <table style="width:100%; margin-top:10px; border-collapse:collapse; font-size:12px;">
                  <thead>
                    <tr style="background:#f9fafb;">
                      <th style="text-align:left; padding:6px; border:1px solid #e5e7eb;">Name</th>
                      <th style="text-align:left; padding:6px; border:1px solid #e5e7eb;">Title</th>
                      <th style="text-align:left; padding:6px; border:1px solid #e5e7eb;">Facility</th>
                      <th style="text-align:left; padding:6px; border:1px solid #e5e7eb;">Location</th>
                      <th style="text-align:left; padding:6px; border:1px solid #e5e7eb;">Phone</th>
                      <th style="text-align:left; padding:6px; border:1px solid #e5e7eb;">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${persons.map((p) => `
                      <tr>
                        <td style="padding:6px; border:1px solid #e5e7eb;">${esc(p.last_name)}, ${esc(p.first_name)}</td>
                        <td style="padding:6px; border:1px solid #e5e7eb;">${esc(p.title)}</td>
                        <td style="padding:6px; border:1px solid #e5e7eb;">${esc(p.facility)}</td>
                        <td style="padding:6px; border:1px solid #e5e7eb;">${esc(p.physical_location)}</td>
                        <td style="padding:6px; border:1px solid #e5e7eb;">${esc(p.phone)}${p.phone_extension ? ` ext. ${esc(p.phone_extension)}` : ''}</td>
                        <td style="padding:6px; border:1px solid #e5e7eb;">${esc(p.email)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : ''}
            </div>
          `;
        }).join('')}
      </div>
    `;
  };

  const generateMultipleEntriesPrint = () => {
    // If the user has explicit selections, honour them; otherwise print every liaison entry.
    const hasSelection = selectedEntries.length > 0;
    const selectedData = hasSelection
      ? liaisonData.filter(entry => selectedEntries.includes(entry.id))
      : liaisonData;

    // Only include organizations when nothing is explicitly selected (i.e. "Print All"/full report).
    const orgsHTML = hasSelection ? '' : generateOrganizationsPrint();
    const orgCount = hasSelection ? 0 : (exerciseOrganizations?.length || 0);

    return `
      <div class="header">
        <h1>Liaison Officer Report</h1>
        <h3>Exercise: ${exerciseName || exerciseId}</h3>
        <p>${selectedData.length} entry(ies)${orgCount ? ` · ${orgCount} organization(s)` : ''}</p>
      </div>
      ${selectedData.map(entry => generateEntryContent(entry)).join('')}
      ${orgsHTML}
    `;
  };

  const toggleEntrySelection = (id) => {
    setSelectedEntries(prev => 
      prev.includes(id) 
        ? prev.filter(entryId => entryId !== id)
        : [...prev, id]
    );
  };

  const filteredData = (activeCategory === 'all' 
    ? (liaisonData || []) 
    : (liaisonData || []).filter(entry => entry.category === activeCategory)
  ).sort((a, b) => {
    // Sort chronologically by date_from (oldest first)
    const da = a.date_from ? new Date(a.date_from) : new Date(a.timestamp || a.created_at || 0);
    const db = b.date_from ? new Date(b.date_from) : new Date(b.timestamp || b.created_at || 0);
    return da - db;
  });

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-900 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-orange-500 mb-2">
              Liaison Officer
            </h1>
            <p className="text-white text-sm">
              Manage interagency coordination, communication, and liaison activities
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => handlePrint()}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Liaison Officer Report
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Entry
            </Button>
            {selectedEntries.length > 0 && (
              <Button
                onClick={() => handlePrint()}
                variant="outline"
                className="border-blue-500 text-blue-500 hover:bg-blue-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Selected ({selectedEntries.length})
              </Button>
            )}
            {liaisonData.length > 0 && (
              <Button
                onClick={() => handlePrint()}
                variant="outline"
                className="border-gray-500 text-gray-500 hover:bg-gray-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Category Navigation */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.entries(categories).map(([key, category]) => {
            const IconComponent = category.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  activeCategory === key
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <IconComponent className="h-4 w-4 mr-2" />
                {category.label}
              </button>
            );
          })}
          {/* Organization Coord Button — mirrors Step 8 Organizations for this exercise */}
          <button
            onClick={() => setActiveCategory('organization-coord')}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              activeCategory === 'organization-coord'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            data-testid="liaison-org-coord-tab"
          >
            <Building className={`h-4 w-4 mr-2 ${activeCategory === 'organization-coord' ? 'text-white' : 'text-blue-300'}`} />
            <span>Organization Coord</span>
          </button>
          {/* Show All Button */}
          <button
            onClick={() => setActiveCategory('all')}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              activeCategory === 'all'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <MessageCircle className={`h-4 w-4 mr-2 ${
              activeCategory === 'all' ? 'text-white' : 'text-red-600'
            }`} />
            <span>Show All</span>
          </button>
        </div>
      </div>

      {/* Entry Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-orange-500">
              {editingEntry ? 'Edit Entry' : 'Add New Entry'}
            </h2>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
              variant="ghost"
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmitLocal} className="space-y-6">
            {/* Entry Image Card - for all liaison categories */}
            <EntryImageCard
              value={formData.image}
              onChange={(img) => setFormData({...formData, image: img})}
              title="Entry Image"
            />

            {/* Title Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Title of this entry"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.title ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>

            {/* Emergency Phase Selection */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Emergency Phase *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              >
                {Object.entries(categories).map(([key, category]) => (
                  <option key={key} value={key} className={category.color}>{category.label}</option>
                ))}
              </select>
            </div>

            {/* Removed Incident Name and Timestamp fields */}

            {/* Operational Period: Date/Time From and Date/Time To */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Operational Period - Date / Time from:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date_from ? formData.date_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_from ? formData.date_from.split('T')[1] : '00:00';
                      setFormData({...formData, date_from: `${date}T${time}`});
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                  <input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_from ? formData.date_from.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_from ? formData.date_from.split('T')[0] : '';
                        setFormData({...formData, date_from: `${date}T${value}`});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Date / Time to:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date_to ? formData.date_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_to ? formData.date_to.split('T')[1] : '00:00';
                      setFormData({...formData, date_to: `${date}T${time}`});
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                  <input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_to ? formData.date_to.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_to ? formData.date_to.split('T')[0] : '';
                        setFormData({...formData, date_to: `${date}T${value}`});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Situation Summary *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                placeholder="a collective description of the current situation"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.description ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            {/* Priority and Status */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Contact Name and Contact Phone */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="First and last name"
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Contact Phone
                </label>
                <input
                  type="text"
                  value={formData.contact_phone}
                  onChange={(e) => {
                    // Only allow digits and auto-format as XXX-XXX-XXXX
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    let formatted = '';
                    if (digits.length > 0) {
                      formatted = digits.slice(0, 3);
                      if (digits.length > 3) {
                        formatted += '-' + digits.slice(3, 6);
                      }
                      if (digits.length > 6) {
                        formatted += '-' + digits.slice(6, 10);
                      }
                    }
                    if (formatted.length <= 12) {
                      setFormData({...formData, contact_phone: formatted});
                    }
                  }}
                  placeholder="204-123-4567"
                  maxLength="12"
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.contact_phone ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.contact_phone && <p className="text-red-500 text-xs mt-1">{errors.contact_phone}</p>}
              </div>
            </div>

            {/* Category-specific fields */}
            {categories[formData.category]?.fields.map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-white mb-1">
                  {field.label} {field.required && '*'}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    rows={3}
                    placeholder={field.placeholder || ''}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                  />
                ) : field.type === 'organization-select' ? (
                  <select
                    value={formData[field.key] || ''}
                    onChange={(e) => {
                      const orgName = e.target.value;
                      const patch = { [field.key]: orgName };
                      if (orgName) {
                        const org = exerciseOrganizations.find((o) => o.name === orgName);
                        if (org) {
                          // Auto-fill: image (only if empty), contact information from persons,
                          // and seed assisting/cooperating orgs with the other Step-8 orgs.
                          if (!formData.image && org.organization_image) {
                            patch.image = org.organization_image;
                          }
                          const persons = Array.isArray(org.persons) ? org.persons : [];
                          if (persons.length > 0) {
                            const lines = persons.map((p) => {
                              const parts = [];
                              const name = `${p.last_name || ''}, ${p.first_name || ''}`.trim().replace(/^,\s*/, '');
                              if (name) parts.push(name);
                              if (p.title) parts.push(p.title);
                              if (p.facility) parts.push(p.facility);
                              if (p.physical_location) parts.push(`📍 ${p.physical_location}`);
                              if (p.phone) parts.push(`📞 ${p.phone}${p.phone_extension ? ` ext. ${p.phone_extension}` : ''}`);
                              if (p.email) parts.push(`✉ ${p.email}`);
                              return parts.join(' · ');
                            });
                            const contactText = lines.join('\n');
                            if (!formData.agency_contact_information || formData.agency_contact_information.trim() === '') {
                              patch.agency_contact_information = contactText;
                            }
                          }
                          const others = exerciseOrganizations
                            .filter((o) => o.name && o.name !== orgName)
                            .map((o) => o.name);
                          if (others.length > 0 && (!formData.assisting_cooperating_agencies || formData.assisting_cooperating_agencies.trim() === '')) {
                            patch.assisting_cooperating_agencies = others.join('\n');
                          }
                        }
                      }
                      setFormData({ ...formData, ...patch });
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                    data-testid="liaison-org-select"
                  >
                    <option value="">{exerciseOrganizations.length === 0 ? 'No organizations in Step 8 yet' : (field.placeholder || 'Select an organization…')}</option>
                    {exerciseOrganizations.map((org) => (
                      <option key={org.id || org.name} value={org.name}>{org.name}</option>
                    ))}
                  </select>
                  ) : (
                  <input
                    type={field.type}
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    placeholder={field.placeholder || ''}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                  />
                )}
                {errors[field.key] && <p className="text-red-500 text-xs mt-1">{errors[field.key]}</p>}
              </div>
            ))}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                placeholder="notes / comments / remarks"
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                variant="outline"
                className="border-gray-500 text-gray-500 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {loading ? 'Saving...' : (editingEntry ? 'Update Entry' : 'Add Entry')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Entries List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-orange-500">
            {activeCategory === 'all'
              ? 'All Entries'
              : activeCategory === 'organization-coord'
                ? `Organization Coordination (${exerciseOrganizations.length})`
                : categories[activeCategory]?.label + ' Entries'}
          </h3>
          {activeCategory === 'organization-coord' && (
            <a
              href={`#builder?exercise=${exerciseId}&step=8`}
              className="text-xs text-blue-400 hover:text-blue-300 underline"
              data-testid="liaison-manage-in-step8"
            >
              Manage in Step 8 →
            </a>
          )}
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className="text-white mt-2">Loading entries...</p>
            </div>
          ) : activeCategory === 'organization-coord' ? (
            <OrganizationsInlineManager exerciseId={exerciseId} />
          ) : (
            <>
              {filteredData.length === 0 && !(activeCategory === 'all' && exerciseOrganizations.length > 0) ? (
                <div className="text-center py-8">
                  <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-orange-500 mb-2">
                    {activeCategory === 'all' ? 'No Liaison Entries Found' : `No ${categories[activeCategory]?.label} Entries`}
                  </h3>
                  <p className="text-white mb-4">
                    {activeCategory === 'all'
                      ? 'Start by adding your first liaison entry in any category for this exercise.'
                      : `Start by adding your first ${categories[activeCategory]?.label.toLowerCase()} entry for this exercise.`
                    }
                  </p>
                </div>
              ) : (
                <SortableEntryList
                  entries={filteredData}
                  setEntries={(newEntries) => {
                    if (activeCategory === 'all') {
                      setLiaisonData(newEntries);
                    } else {
                      const otherEntries = liaisonData.filter(d => d.category !== activeCategory);
                      setLiaisonData([...otherEntries, ...newEntries]);
                    }
                  }}
                  reorderEndpoint="liaison-officer/reorder"
                  renderEntry={(entry) => {
                    const priorityOption = priorityOptions.find(p => p.value === entry.priority);
                    const statusOption = statusOptions.find(s => s.value === entry.status);
                
                return (
                  <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Checkbox
                            checked={selectedEntries.includes(entry.id)}
                            onCheckedChange={() => toggleEntrySelection(entry.id)}
                          />
                          {entry.image && (
                            <img
                              src={entry.image}
                              alt="Agency"
                              className="w-[60px] h-[60px] rounded object-cover border border-gray-500 flex-shrink-0"
                              data-testid={`agency-entry-image-${entry.id}`}
                            />
                          )}
                          <h4 className="text-lg font-semibold text-white">{entry.title}</h4>
                          <Badge className={`${priorityOption?.color} bg-transparent border`}>
                            {priorityOption?.label}
                          </Badge>
                          <Badge className={`${statusOption?.color} bg-transparent border`}>
                            {statusOption?.label}
                          </Badge>
                        </div>
                        
                        <p className="text-gray-300 mb-3">{entry.description}</p>
                        
                        {/* Operational Period */}
                        {(entry.date_from || entry.date_to) && (
                          <div className="flex items-center gap-4 mb-3 text-sm bg-gray-600/40 rounded-lg px-3 py-2" data-testid="liaison-op-period">
                            <div>
                              <span className="text-gray-400 font-medium">Op Period From:</span>{' '}
                              <span className="text-white">{entry.date_from ? entry.date_from.replace('T', ' ').slice(0, 16) : 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400 font-medium">To:</span>{' '}
                              <span className="text-white">{entry.date_to ? entry.date_to.replace('T', ' ').slice(0, 16) : 'N/A'}</span>
                            </div>
                          </div>
                        )}
                        
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-400">
                          {entry.location && (
                            <div>
                              <span className="font-medium">Location:</span> {entry.location}
                            </div>
                          )}
                          {(entry.contact_info || entry.contact_name) && (
                            <div>
                              <span className="font-medium">Contact:</span> {entry.contact_name || entry.contact_info}{entry.contact_phone ? ` (${entry.contact_phone})` : ''}
                            </div>
                          )}
                        </div>

                        {/* Category-specific fields */}
                        {(() => {
                          const catFields = categories[entry.category]?.fields || [];
                          const populated = catFields.filter(f => entry[f.key]);
                          if (populated.length === 0) return null;
                          return (
                            <div className="mt-3 grid md:grid-cols-2 gap-2">
                              {populated.map(f => (
                                <div key={f.key} className="text-sm p-2 bg-gray-600/30 rounded">
                                  <span className="text-gray-400 font-medium">{f.label}:</span>{' '}
                                  <span className="text-gray-300">{entry[f.key]}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        
                        {entry.notes && (
                          <div className="mt-3 p-3 bg-gray-600 rounded text-sm text-gray-300">
                            <span className="font-medium">Notes:</span> {entry.notes}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2 ml-4">
                        <Button
                          onClick={() => handlePrint(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleEdit(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-orange-400 hover:text-orange-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(entry.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
              )}
              {activeCategory === 'all' && exerciseOrganizations.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-700" data-testid="liaison-all-orgs-section">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-md font-semibold text-blue-400 flex items-center">
                      <Building className="h-4 w-4 mr-2" />
                      Organization Coordination ({exerciseOrganizations.length})
                    </h4>
                    <button
                      onClick={() => setActiveCategory('organization-coord')}
                      className="text-xs text-blue-400 hover:text-blue-300 underline"
                    >
                      Manage →
                    </button>
                  </div>
                  <div className="space-y-3">
                    {exerciseOrganizations.map((org) => {
                      const persons = Array.isArray(org.persons) ? org.persons : [];
                      return (
                        <div key={org.id} className="bg-gray-700 rounded-lg p-4 border border-gray-600" data-testid={`liaison-all-org-${org.id}`}>
                          <div className="flex items-start gap-3">
                            <div className="w-14 h-14 rounded bg-gray-600 flex-shrink-0 overflow-hidden">
                              {org.organization_image ? (
                                <img src={org.organization_image} alt={org.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400"><Building className="h-7 w-7" /></div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-semibold">{org.name}
                                {persons.length > 0 && <span className="ml-2 text-xs text-gray-400">({persons.length} {persons.length === 1 ? 'person' : 'persons'})</span>}
                              </h5>
                              {org.description && <p className="text-gray-300 text-sm mt-1">{org.description}</p>}
                              {persons.length > 0 && (
                                <div className="mt-2 grid md:grid-cols-2 gap-2">
                                  {persons.map((p) => (
                                    <div key={p.id} className="bg-gray-800 rounded p-2 text-xs">
                                      <div className="text-white font-semibold">{p.last_name}, {p.first_name}
                                        {p.title && <span className="ml-2 px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300">{p.title}</span>}
                                        {p.facility && <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">{p.facility}</span>}
                                      </div>
                                      <div className="text-gray-400 mt-1">
                                        {p.physical_location && <span>📍 {p.physical_location} </span>}
                                        {p.phone && <span>· 📞 {p.phone}{p.phone_extension ? ` ext. ${p.phone_extension}` : ''} </span>}
                                        {p.email && <span>· ✉ {p.email}</span>}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Scenario Module Component

export default LiaisonOfficerModule;
